/**
 * How an installed app says which build it is, on every request it sends.
 *
 * The website is deployed with the API and is always its match. An installed
 * app is not: a phone runs whatever build it last installed, weeks or years
 * ago, so the server has to know which one is asking before it can answer. The
 * native half sends this header from its own HTTP client, and the SPA inside the
 * app sends it too - the value comes from the native side, never from the
 * bundle. A browser sends nothing, and nothing is ever refused for its absence.
 *
 * **Frozen, like `app.update_required`.** Every build ever shipped sends this
 * header in exactly this form, so the server has to go on reading it: change
 * the name or the format and old apps become unidentifiable, which is the one
 * thing that would stop them being told to update.
 */
/** Lower case, as Node hands header names to Fastify. */
export const APP_VERSION_HEADER = 'x-hb-app';
/** The platforms an installed app is built for. */
export const APP_PLATFORMS = ['android'];
/** The header's value: `<platform>/<build>`, e.g. `android/12`. */
export function formatAppVersion(version) {
    return `${version.platform}/${String(version.build)}`;
}
/**
 * The build named by a header value, or null for anything else.
 *
 * Null rather than a refusal for a value that is malformed or names a platform
 * nobody builds for: the header only ever takes away, so a request nobody can
 * identify is treated like a browser's - answered - rather than locked out.
 *
 * Takes the header as a server is handed it. Node joins a header sent twice
 * into one string, which then matches nothing; a list only comes from something
 * that built the headers itself, such as `inject()` in a test, and its first
 * value is the one read.
 */
export function parseAppVersion(header) {
    const value = typeof header === 'string' ? header : header?.[0];
    // Ten digits at most: Play's highest versionCode is 2,100,000,000.
    const match = /^([a-z]+)\/(\d{1,10})$/u.exec(value?.trim() ?? '');
    if (!match)
        return null;
    const platform = APP_PLATFORMS.find((known) => known === match[1]);
    return platform === undefined ? null : { platform, build: Number(match[2]) };
}
//# sourceMappingURL=app-version.js.map